// src/routes/MyFranchise.tsx
import { useMutation, useQuery } from 'convex/react';
import { useEffect, useState } from 'react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';

const DROP_PENALTY_PER_YEAR = 5;
function previewPenalty(yearsRemaining: number) {
  return Math.max(0, yearsRemaining - 1) * DROP_PENALTY_PER_YEAR;
}

export default function MyFranchise() {
  const me = useQuery(api.franchises.myFranchise);
  const roster = useQuery(api.rosters.getRoster, {});
  const balance = useQuery(api.bones.getBalance, {});
  const ledger = useQuery(api.bones.recentLedger, { limit: 10 });
  const dropPlayer = useMutation(api.rosters.dropPlayer);
  const autoBind = useMutation(api.franchises.autoBindFranchise);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Id<'players'> | null>(null);
  // null = not attempted / in flight; true = bound (query will re-run);
  // false = no franchise assigned to this email.
  const [autoBindMatched, setAutoBindMatched] = useState<boolean | null>(null);

  // Auto-bind: when signed in but unbound, bind the franchise whose
  // ownerEmail matches the signer. `me` flips reactively on success.
  useEffect(() => {
    if (me !== null || autoBindMatched !== null) return;
    autoBind()
      .then((id) => setAutoBindMatched(id !== null))
      .catch(() => setAutoBindMatched(false));
  }, [me, autoBindMatched, autoBind]);

  if (me === undefined) return <div className="p-6">Loading…</div>;
  if (me === null) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">No franchise</h1>
        {autoBindMatched === false ? (
          <p className="mt-2 text-sm text-gray-600">
            No franchise is assigned to your email address. Contact the commissioner to get set up.
          </p>
        ) : (
          <p className="mt-2 text-sm text-gray-600">Linking your franchise…</p>
        )}
      </div>
    );
  }

  async function handleDrop(playerId: Id<'players'>, name: string, years: number) {
    const penalty = previewPenalty(years);
    if (!window.confirm(`Drop ${name}? Years remaining: ${years}. Penalty: ${penalty} bones.`)) {
      return;
    }
    setError(null);
    setPending(playerId);
    try {
      await dropPlayer({ playerId });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPending(null);
    }
  }

  const totalYears = roster?.reduce((sum, r) => sum + r.contract.yearsRemaining, 0) ?? 0;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold">{me.name}</h1>
      <div className="mt-2 flex gap-6 text-sm text-gray-600">
        <span>Division: {me.division}</span>
        <span>
          Roster: <strong>{roster?.length ?? 0}</strong> / 25
        </span>
        <span>
          Contract years: <strong>{totalYears}</strong> / 50
        </span>
        <span>
          Bones: <strong>{balance ?? '—'}</strong>
        </span>
      </div>
      {error && (
        <div className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      <h2 className="mt-6 text-lg font-semibold">Roster</h2>
      <table className="mt-2 w-full border-collapse">
        <thead>
          <tr className="border-b text-left text-sm text-gray-500">
            <th className="py-2">Player</th>
            <th>Pos</th>
            <th>Team</th>
            <th>Years</th>
            <th>Signed</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {roster?.map(({ contract, player }) => (
            <tr key={contract._id} className="border-b">
              <td className="py-2">{player.name}</td>
              <td>{player.position}</td>
              <td>{player.nflTeam}</td>
              <td>{contract.yearsRemaining}</td>
              <td className="text-sm text-gray-500">{contract.signedFor}</td>
              <td className="text-right">
                <button
                  type="button"
                  onClick={() => handleDrop(player._id, player.name, contract.yearsRemaining)}
                  disabled={pending === player._id}
                  className="text-sm text-red-600 hover:underline disabled:opacity-50"
                >
                  {pending === player._id
                    ? 'Dropping…'
                    : `Drop (-${previewPenalty(contract.yearsRemaining)})`}
                </button>
              </td>
            </tr>
          ))}
          {roster?.length === 0 && (
            <tr>
              <td colSpan={6} className="py-4 text-sm text-gray-500">
                Empty roster.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <h2 className="mt-6 text-lg font-semibold">Recent activity</h2>
      <ul className="mt-2 space-y-1 text-sm">
        {ledger?.map((row) => (
          <li key={row._id}>
            <span className={row.delta < 0 ? 'text-red-600' : 'text-green-700'}>
              {row.delta > 0 ? '+' : ''}
              {row.delta}
            </span>{' '}
            <span className="text-gray-500">— {row.reason}</span>
          </li>
        ))}
        {ledger?.length === 0 && <li className="text-gray-500">no activity yet.</li>}
      </ul>
    </div>
  );
}
